import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { fmtDateTime } from "../lib/format";
import {
  SectionHeading,
  StatusBadge,
  Loading,
  EmptyState,
  ErrorBanner,
} from "../components/ui";

interface AppRow {
  id: string;
  eventId: string;
  applicationStatus: string;
  participationMode: string;
  appliedAt: string;
  withSocial: boolean;
  cancelledAt: string | null;
  eventTitle: string;
  eventStartAt: string | null;
}

const APP_STATUS_LABELS: Record<string, string> = {
  pending_payment: "決済待ち",
  applied: "申込中",
  cancelled: "キャンセル",
  checked_in: "受付済み",
  no_show: "不参加",
};
const APP_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral" | "accent"
> = {
  pending_payment: "warning",
  applied: "accent",
  cancelled: "neutral",
  checked_in: "success",
  no_show: "danger",
};

export function EventsMinePage() {
  const [items, setItems] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ items: AppRow[] }>("/events/me/applications");
        setItems(r.items);
      } catch (e) {
        setErr((e as Error).message ?? "取得に失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = Date.now();
  const upcoming = useMemo(
    () =>
      items
        .filter(
          (a) =>
            a.applicationStatus !== "cancelled" &&
            a.eventStartAt &&
            new Date(a.eventStartAt).getTime() >= now,
        )
        .sort(
          (x, y) =>
            new Date(x.eventStartAt!).getTime() -
            new Date(y.eventStartAt!).getTime(),
        ),
    [items, now],
  );
  const past = useMemo(
    () =>
      items
        .filter(
          (a) =>
            a.applicationStatus === "cancelled" ||
            !a.eventStartAt ||
            new Date(a.eventStartAt).getTime() < now,
        )
        .sort(
          (x, y) =>
            new Date(y.eventStartAt ?? y.appliedAt).getTime() -
            new Date(x.eventStartAt ?? x.appliedAt).getTime(),
        ),
    [items, now],
  );

  const Row = (a: AppRow) => (
    <Link
      key={a.id}
      to={`/events/${a.eventId}`}
      className="flex items-start gap-3 py-3 hover:bg-black/[0.02] transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="font-bold text-tora-ink leading-snug">{a.eventTitle}</p>
        <p className="text-xs text-tora-ink/60 mt-0.5">
          📅 {fmtDateTime(a.eventStartAt)}
          {a.withSocial && <span className="ml-2">🍻 懇親会あり</span>}
        </p>
      </div>
      <StatusBadge
        tone={APP_STATUS_TONE[a.applicationStatus] ?? "neutral"}
        bold={a.applicationStatus === "checked_in"}
      >
        {APP_STATUS_LABELS[a.applicationStatus] ?? a.applicationStatus}
      </StatusBadge>
    </Link>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SectionHeading level={2}>参加予定</SectionHeading>
        <Link to="/events" className="tap-target text-sm text-tora hover:underline">
          イベント一覧へ
        </Link>
      </div>

      {err && <ErrorBanner>{err}</ErrorBanner>}
      {loading ? (
        <Loading />
      ) : (
        <>
          <section className="card p-5 md:p-6">
            <h3 className="text-base font-bold text-tora mb-2">これからの参加</h3>
            {upcoming.length === 0 ? (
              <EmptyState icon="🗓" title="参加予定のイベントはありません" />
            ) : (
              <div className="divide-y divide-black/5">{upcoming.map(Row)}</div>
            )}
          </section>

          {past.length > 0 && (
            <section className="card p-5 md:p-6">
              <h3 className="text-base font-bold text-tora-ink/70 mb-2">
                過去・キャンセル
              </h3>
              <div className="divide-y divide-black/5">{past.map(Row)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
